import { useEffect, useState } from "react";
import api, { money } from "../../../../lib/api";
import { SectionHeading, MetricCard } from "../shared";
import { Button } from "../../../../components/ui/button";
import { toast } from "sonner";
import { Download, Filter } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

/**
 * AnalyticsTab — the dropship-only analytics view. Revenue, orders, AOV,
 * fulfillment rate, top customers with a period switcher.
 * Extracted from SellerDropship.jsx in Phase 5 refactor.
 * Iter 190 — added Postage-wallet spend timeline + by-carrier breakdown.
 */
export default function AnalyticsTab() {
  const [period, setPeriod] = useState("mtd");
  const [data, setData] = useState(null);
  const [postageDays, setPostageDays] = useState(30);
  const [postage, setPostage] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerCategory, setLedgerCategory] = useState("");
  const loadLedger = () => {
    const params = new URLSearchParams({ days: String(postageDays) });
    if (ledgerCategory) params.set("category", ledgerCategory);
    api.get(`/postage-wallet/ledger?${params}`)
      .then(r => setLedger(r.data))
      .catch(() => setLedger(null));
  };
  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ days: String(postageDays), format: "csv" });
      if (ledgerCategory) params.set("category", ledgerCategory);
      const r = await api.get(`/postage-wallet/ledger?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verqo-postage-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ledger downloaded");
    } catch {
      toast.error("Export failed");
    }
  };
  useEffect(() => { api.get(`/dropship/analytics?period=${period}`).then(r => setData(r.data)).catch(() => {}); }, [period]);
  useEffect(() => { api.get(`/postage-wallet/analytics?days=${postageDays}`).then(r => setPostage(r.data)).catch(() => setPostage(null)); }, [postageDays]);
  useEffect(() => { loadLedger(); /* eslint-disable-next-line */ }, [postageDays, ledgerCategory]);

  const c2 = (v) => `$${(Number(v || 0) / 100).toFixed(2)}`;
  const daily = (postage?.daily_spend || []).map(d => ({
    date: d.date.slice(5),           // MM-DD for compact axis
    spend: d.spend_cents / 100,
    refund: d.refund_cents / 100,
  }));
  const carriers = postage?.by_carrier || [];
  const topCarrierSpend = carriers[0]?.spend_cents || 1;

  return (
    <div className="space-y-5">
      <SectionHeading
        title="Dropship analytics"
        sub="Revenue, orders, AOV, fulfillment rate, top customers — all your dropship-only signals."
        action={
          <select value={period} onChange={e => setPeriod(e.target.value)} data-testid="analytics-period" className="h-10 rounded-full border border-border bg-white px-4 text-[13px]">
            <option value="mtd">Month to date</option>
            <option value="last30">Last 30 days</option>
            <option value="qtd">Quarter to date</option>
            <option value="ytd">Year to date</option>
          </select>
        }
      />
      {!data ? <div className="text-foreground/50 text-[13px]">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Revenue" value={money(data.revenue)}/>
            <MetricCard label="Orders" value={data.orders}/>
            <MetricCard label="Units" value={data.units}/>
            <MetricCard label="AOV" value={money(data.aov)}/>
            <MetricCard label="Fulfillment rate" value={`${data.fulfillment_rate}%`}/>
            <MetricCard label="Delivered" value={data.delivered}/>
            <MetricCard label="Status mix" value={Object.keys(data.status_breakdown || {}).length}/>
          </div>
          {data.top_customers?.length > 0 && (
            <div className="rounded-2xl border border-border bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-border text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Top customers</div>
              <div className="grid grid-cols-[24px_1.5fr_1fr_80px_80px_100px] gap-3 px-5 py-2 bg-secondary/30 text-[10px] mono uppercase tracking-[0.16em] text-foreground/55">
                <div>#</div><div>Customer</div><div>Location</div><div className="text-right">Orders</div><div className="text-right">Units</div><div className="text-right">Spent</div>
              </div>
              <div className="divide-y divide-border">
                {data.top_customers.map((c, i) => (
                  <div key={i} className="grid grid-cols-[24px_1.5fr_1fr_80px_80px_100px] gap-3 px-5 py-2.5 items-center text-[13px]">
                    <div className="text-foreground/45 mono">{i + 1}</div>
                    <div>{c.name}</div>
                    <div className="text-[12px] text-foreground/65 truncate">{c.city}</div>
                    <div className="text-right mono">{c.orders}</div>
                    <div className="text-right mono">{c.units}</div>
                    <div className="text-right font-semibold">{money(c.spent)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Postage-wallet analytics — Iter 190 */}
          <div className="pt-3">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-[15px] font-semibold text-foreground">Postage spend</div>
                <div className="text-[12px] text-foreground/60 mt-0.5">Wallet debits, refunds, and per-carrier breakdown from the Verqo Postage Wallet.</div>
              </div>
              <select
                value={postageDays} onChange={e => setPostageDays(Number(e.target.value))}
                data-testid="postage-analytics-period"
                className="h-10 rounded-full border border-border bg-white px-4 text-[13px]"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 12 months</option>
              </select>
            </div>

            {postage?.totals && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <MetricCard label="Postage spent" value={c2(postage.totals.debits_cents)}/>
                <MetricCard label="Refunded" value={c2(postage.totals.refunds_cents)}/>
                <MetricCard label="Net out" value={c2(postage.totals.net_out_cents)}/>
                <MetricCard label="Labels bought" value={postage.totals.label_count}/>
              </div>
            )}

            <div className="rounded-2xl border border-border bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-border text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Daily spend</div>
              <div className="p-4" style={{ height: 240 }}>
                {daily.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[12px] text-foreground/50">
                    No postage activity yet — top up your wallet and start shipping to see daily spend.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af"/>
                      <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af"
                             tickFormatter={v => `$${v}`}/>
                      <Tooltip
                        formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === "spend" ? "Spent" : "Refunded"]}
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      />
                      <Bar dataKey="spend" fill="#4f46e5" name="spend" radius={[4, 4, 0, 0]}/>
                      <Bar dataKey="refund" fill="#10b981" name="refund" radius={[4, 4, 0, 0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {carriers.length > 0 && (
              <div className="rounded-2xl border border-border bg-white overflow-hidden mt-4">
                <div className="px-5 py-3 border-b border-border text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">By carrier</div>
                <div className="divide-y divide-border">
                  {carriers.map(c => (
                    <div key={c.carrier} className="px-5 py-3">
                      <div className="flex items-center justify-between text-[13px]">
                        <div className="font-medium capitalize">{c.carrier}</div>
                        <div className="text-right">
                          <span className="font-semibold tabular-nums">{c2(c.spend_cents)}</span>
                          <span className="text-[11px] text-foreground/50 ml-2">· {c.orders} label{c.orders === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-secondary/60 overflow-hidden">
                        <div
                          className="h-full bg-indigo-600 rounded-full"
                          style={{ width: `${Math.round((c.spend_cents / topCarrierSpend) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full ledger — unified accounting view across main + postage wallet */}
            <div className="rounded-2xl border border-border bg-white overflow-hidden mt-4" data-testid="postage-full-ledger">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[13px] font-semibold">Full postage ledger</div>
                  <div className="text-[10.5px] text-foreground/55 mt-0.5">Every postage-related movement across both wallets. Paired rows share the same ref (e.g., POST-XXXXXX).</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Filter className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/40"/>
                    <select
                      value={ledgerCategory} onChange={e => setLedgerCategory(e.target.value)}
                      data-testid="ledger-category-filter"
                      className="h-9 pl-7 pr-3 rounded-md border border-border bg-white text-[12px] font-medium"
                    >
                      <option value="">All categories</option>
                      <option value="postage_funding">Postage funds</option>
                      <option value="shipping">Shipping</option>
                      <option value="shipping_refund">Refunds</option>
                    </select>
                  </div>
                  <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="ledger-export-csv" className="h-9">
                    <Download className="w-3.5 h-3.5 mr-1.5"/>CSV
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-[100px_80px_160px_120px_100px_120px_1fr] gap-2 px-4 py-2 bg-secondary/30 text-[10px] mono uppercase tracking-[0.14em] text-foreground/55">
                <div>Date</div>
                <div>Wallet</div>
                <div>Kind</div>
                <div>Category</div>
                <div className="text-right">Amount</div>
                <div>Ref</div>
                <div>Description</div>
              </div>
              {!ledger ? (
                <div className="p-6 text-center text-[12px] text-foreground/50">Loading…</div>
              ) : ledger.rows?.length === 0 ? (
                <div className="p-8 text-center text-[12px] text-foreground/50">No postage activity in this period.</div>
              ) : (
                <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                  {ledger.rows.map((r, i) => {
                    const cat = r.category || "other";
                    const catStyle = {
                      postage_funding: { bg: "bg-indigo-50",  fg: "text-indigo-700",  label: "Postage funds" },
                      shipping:        { bg: "bg-rose-50",    fg: "text-rose-700",    label: "Shipping" },
                      shipping_refund: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Refund" },
                      other:           { bg: "bg-slate-100",  fg: "text-slate-600",   label: "Other" },
                    }[cat] || { bg: "bg-slate-100", fg: "text-slate-600", label: cat };
                    return (
                      <div key={`${r.ref}-${i}`} className="grid grid-cols-[100px_80px_160px_120px_100px_120px_1fr] gap-2 px-4 py-2 items-center text-[11.5px] hover:bg-muted/20">
                        <div className="text-foreground/60 tabular-nums">{(r.created_at || "").slice(5, 16).replace("T", " ")}</div>
                        <div>
                          <span className={`text-[10px] mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${
                            r.source_wallet === "main" ? "bg-slate-100 text-slate-700" : "bg-indigo-100 text-indigo-800"
                          }`}>{r.source_wallet}</span>
                        </div>
                        <div className="capitalize truncate">{(r.kind || "").replace(/_/g, " ")}</div>
                        <div>
                          <span className={`text-[9.5px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold ${catStyle.bg} ${catStyle.fg}`}>
                            {catStyle.label}
                          </span>
                        </div>
                        <div className={`text-right tabular-nums font-semibold ${r.amount_cents >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {r.amount_cents >= 0 ? "+" : "-"}${Math.abs(r.amount_cents / 100).toFixed(2)}
                        </div>
                        <div className="mono text-[10.5px] text-foreground/60 truncate">{r.ref || "—"}</div>
                        <div className="text-foreground/70 truncate" title={r.note || r.meta?.description || ""}>
                          {r.meta?.description || r.note || (r.meta?.order_code && `Order ${r.meta.order_code}`) || "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {ledger?.rows?.length > 0 && (
                <div className="px-4 py-2 border-t border-border text-[11px] text-foreground/55 flex items-center justify-between">
                  <span>{ledger.count} rows over last {ledger.days} days</span>
                  <span className="flex gap-4 tabular-nums">
                    <span>Funding: ${((ledger.totals?.postage_funding || 0) / 100).toFixed(2)}</span>
                    <span>Shipping: ${((ledger.totals?.shipping || 0) / 100).toFixed(2)}</span>
                    <span>Refunds: ${((ledger.totals?.shipping_refund || 0) / 100).toFixed(2)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
